// Class display names (v3.71.0).
//
// One module owns how a class name is rendered — every page routes through
// here so the rules can never drift per-page:
//
//   precedence: displayNameOverride (teacher/admin rename, non-empty string)
//             → formatted name        (Clever-sourced classes only)
//             → raw base name
//
// The base name preserves each scheme's source of truth:
//   - manual/legacy classes: the doc-id prefix (`${name}_${teacherUid}`) —
//     BYTE-IDENTICAL to the historical `classId.split('_')[0]` rendering;
//     the formatter NEVER runs on these.
//   - Clever classes: the sync-owned `className` field first (doc ids are
//     never re-keyed on a district-side section rename, so the id prefix
//     goes stale; `className` is refreshed every sync), id prefix fallback.
//
// The formatter drops ONLY a trailing period-list segment from the Infinite
// Campus "Course - Teacher - Period(s)" pattern. Validated read-only against
// all 113 live Elmbrook section names on 2026-09-01: 113 shortened, 0
// misses, 0 label collisions. Deliberately restrictive: a lone word never
// matches, so "Grade 2 - Reading - Johnson" can never lose "Johnson".
// Do NOT widen these shapes without a fresh staging-name inventory
// (owner guardrail, Step 0 of the v3.71.0 design).

import { isCleverRosterDoc } from './rosterSource';

/** Max override length — mirrored in firestore.rules
 *  displayNameOverrideValidIfTouched(); keep the two in sync. */
export const DISPLAY_NAME_OVERRIDE_MAX = 60;

const NUMERIC_TOKEN = /^\d{1,4}[A-Za-z]?$/; // "2", "0", "7A"
const PERIOD_WORD = /^(activity|activities)$/i; // both appear in live data

function isPeriodListSegment(segment) {
  const tokens = segment.split(',').map((t) => t.trim());
  if (tokens.length === 0 || tokens.some((t) => t === '')) return false;
  if (tokens.every((t) => NUMERIC_TOKEN.test(t))) return true; // "2", "1,2,3"
  // Mixed lists need 2+ tokens — a lone word is never a period list.
  return tokens.length >= 2
    && tokens.every((t) => NUMERIC_TOKEN.test(t) || PERIOD_WORD.test(t));
}

/**
 * Pure formatter for Clever section names. Callers are responsible for the
 * Clever check — prefer resolveClassLabel/buildClassLabelMap, which apply it.
 */
export function formatClassDisplayName(name) {
  if (typeof name !== 'string') return name;
  const segments = name.split(' - ');
  if (segments.length >= 3 && isPeriodListSegment(segments[segments.length - 1])) {
    return segments.slice(0, -1).join(' - ');
  }
  return name;
}

/** The raw (unformatted, un-overridden) name — also the collision fallback. */
export function classBaseName(classId, classData) {
  const idPrefix = String(classId ?? '').split('_')[0];
  if (isCleverRosterDoc(classData)) return classData.className || idPrefix;
  return idPrefix;
}

function overrideOf(classData) {
  const v = classData && classData.displayNameOverride;
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/** Display label for one class. `classData` may be undefined (e.g. an
 *  assessment referencing an archived class) — falls back to the id prefix. */
export function resolveClassLabel(classId, classData) {
  const override = overrideOf(classData);
  if (override) return override;
  const base = classBaseName(classId, classData);
  return isCleverRosterDoc(classData) ? formatClassDisplayName(base) : base;
}

/**
 * Labels for one rendered list, with deterministic collision fallback:
 * if two classes SHORTEN to the same label, the colliding set renders the
 * fuller base name instead (display-time only, nothing stored). Overrides
 * are the user's explicit choice and are never widened; classes whose raw
 * names are identical in the SIS necessarily render identically.
 *
 * @param {Object<string, object>|Array<{id: string}>} classes classesMap
 *        object (id → data) or an array of docs carrying `id`.
 * @returns {Object<string, string>} id → label
 */
export function buildClassLabelMap(classes) {
  const entries = Array.isArray(classes)
    ? classes.map((c) => [c.id, c])
    : Object.entries(classes || {});
  const labels = {};
  const byLabel = {};
  entries.forEach(([id, data]) => {
    const label = resolveClassLabel(id, data);
    labels[id] = label;
    (byLabel[label] = byLabel[label] || []).push([id, data]);
  });
  Object.values(byLabel).forEach((group) => {
    if (group.length < 2) return;
    group.forEach(([id, data]) => {
      if (!overrideOf(data)) labels[id] = classBaseName(id, data);
    });
  });
  return labels;
}

/**
 * Label for sites that hold ONLY a classId (no class doc loaded — the
 * assessment pickers derive ids from student docs; approved design adds no
 * queries there). Clever provenance is read from the id shape itself:
 * importer ids are `${name}_clever-${sectionId}` and the rules forbid manual
 * classes squatting that shape, so `_clever-` in the id is authoritative.
 * Overrides live on the class doc and therefore do NOT apply here — sites
 * with loaded docs use resolveClassLabel/buildClassLabelMap instead.
 */
export function classLabelFromId(classId) {
  const id = String(classId ?? '');
  const idPrefix = id.split('_')[0];
  return id.includes('_clever-') ? formatClassDisplayName(idPrefix) : idPrefix;
}

/** Convenience for sites holding a labelMap plus loose classIds: map hit
 *  first, id-derived label (formatted when Clever-shaped) as the fallback
 *  for ids missing from the map (archived years etc.). */
export function classLabelFrom(labelMap, classId) {
  return (labelMap && labelMap[classId]) || classLabelFromId(classId);
}
